import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css'; // Import AOS styles
import Statistics from '../components/Statistics';
import ServicesButtonList from '../components/ServicesButtonList';
import { Helmet } from 'react-helmet';
import Schema from '../components/Schema';

export const Home = () => {

  useEffect(() => {
    AOS.init({
      duration: 1000, // animation duration in ms
      easing: 'ease-in-out', // animation easing
      once: true, // whether animation should happen only once - while scrolling down
    });
  }, []);

  const [expanded, setExpanded] = useState(false);

  const items = [
    "Dust, pollen, or bird droppings that prevent sunlight from hitting the panels.",
    "Hail, branches, or other falling debris that crack exposed PV modules.",
    "Hot spots and electrical shortages that form within the panels themselves.",
    "Slipped modules, disconnected wires, or other misaligned components.",
    "Corrosion, rust, rodent damages, and even excessive bee wax accumulation.",
  ];

  const visibleItems = expanded ? items : items.slice(0, 1);

  // ----------- JSON-LD Structured Data ------------
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Solar Care International Pvt Ltd",
    "url": "https://www.solarcareinternational.lk/",
    "logo": "https://www.solarcareinternational.lk/Images/sci-1024x1024.png",
    "description":
      "Solar Care International is Sri Lanka’s #1 provider of preventive maintenance, installations, and bird proofing solutions for solar panels. Trusted in Sri Lanka & Maldives.",
    "sameAs": [
      "https://web.facebook.com/profile.php?id=100091148180919",
      "https://www.instagram.com/solar_care_international/",
      "https://www.youtube.com/@SolarCareInternational",
      "https://www.tiktok.com/@solarcareinternational",
      "https://wa.me/94777129021"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+94-77-712-9021",
        "contactType": "Best Solar Preventive Maintainers",
        "areaServed": ["LK", "MV"],
        "availableLanguage": ["English", "Sinhala", "Tamil"]
      },
      {
        "@type": "ContactPoint",
        "telephone": "+94-11-419-9225",
        "contactType": "Customer Support",
        "areaServed": ["LK", "MV"],
        "availableLanguage": ["English", "Sinhala", "Tamil"]
      }
    ],
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "Sri Rahula Maha Vidyalaya, No.11,12",
        "addressLocality": "Sri Jayawardenepura Kotte",
        "postalCode": "10100",
        "addressCountry": "LK"
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "44/1A, Chapel Lane",
        "addressLocality": "Nugegoda",
        "postalCode": "10250",
        "addressCountry": "LK"
      }
    ],
    "openingHours": "Mo-Su 07:00-19:00"
  };

  return (
    <>
    {/* SEO Head Section */}
    <Schema />
      <Helmet>
        <title>
          Solar Care International | Sri Lanka’s #1 Solar Preventive Maintenance
        </title>
        <meta
          name="description"
          content="Solar Care International is Sri Lanka’s #1 provider of preventive maintenance, solar installations, and bird proofing solutions. Trusted across Sri Lanka & Maldives."
        />
        <meta
          name="keywords"
          content="solar preventive maintenance Sri Lanka, solar panel cleaning, solar installation Sri Lanka, solar bird proofing, renewable energy Sri Lanka, Maldives solar solutions"
        />
        <meta
          property="og:title"
          content="Solar Care International | Sri Lanka’s #1 Solar Preventive Maintenance"
        />
        <meta
          property="og:description"
          content="Trusted solar solutions for Sri Lanka & Maldives: preventive maintenance, solar installations, and cleaning systems."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.solarcareinternational.lk/" />
        <meta
          property="og:image"
          content="https://www.solarcareinternational.lk/Images/sci-1024x1024.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Solar Care International | Sri Lanka’s #1 Solar Preventive Maintenance"
        />
        <meta
          name="twitter:description"
          content="We provide cutting-edge solar preventive maintenance and installation services in Sri Lanka and Maldives."
        />
        <meta
          name="twitter:image"
          content="https://www.solarcareinternational.lk/Images/sci-1024x1024.png"
        />
        <link rel="canonical" href="https://www.solarcareinternational.lk/" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
    <div className="relative h-screen overflow-hidden">
    <video 
  autoPlay 
  loop 
  muted 
  className="absolute top-0 left-0 w-full h-[92vh] object-cover"
>
  <source src="https://res.cloudinary.com/dwf8ifbzs/video/upload/v1749466927/WhatsApp_Video_2025-06-09_at_4.11.50_PM_1_xx57ge.mp4" type="video/mp4" />
  Your browser does not support the video tag.
</video>

 {/* Black Transparent Overlay */}
 <div className="absolute top-0 left-0 w-full h-[92vh] bg-black bg-opacity-40 z-0"></div> 
      <div className="relative isolate px-6 pt-14 lg:px-10" >

        <div className="py-4 items-center justify-center text-center">
        <h1
        className="text-4xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl mb-8 font-bold tracking-tight leading-snug text-white text-center mt-4"
        data-aos="fade-up"
      >
    Elevate Your Energy with<br/> Cutting-Edge Solar Solutions
  </h1>

  <p
    className="text-md sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-4xl mb-8 tracking-tight leading-snug text-white text-center mt-10"
        data-aos="fade-up"
  >
    We empower sustainable futures with<br/> innovative solar solutions, driving the adoption of<br/> renewable energy in Sri Lanka and the Maldives.
  </p>

  <div className="py-6" data-aos="fade-up">
    <a
      href="https://www.youtube.com/embed/qDponbzzbZE"
      target="_blank"
      rel="noopener noreferrer"
    >
      <button
        className="px-4 py-2 bg-white text-black font-bold rounded-full transition-transform transform-gpu hover:-translate-y-1 hover:shadow-lg"
      >
        Watch me!
      </button>
    </a>
  </div>
</div>


          </div>
        </div>


      <div className="flex flex-col items-center text-center px-4">
      <h1
  className="text-4xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl mb-8 font-bold tracking-tight leading-snug text-black"
  data-aos="fade-up"
>
  <span className="text-red-600">#NO.1</span> For Solar <br />
  Preventive Maintenance <br />
  In Sri Lanka
</h1>


<h2
  className="text-2xl sm:text-md md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl mb-12 font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#a1c4fd] via-[#fbc2eb] to-[#fddb92]"
  data-aos="fade-up"
>
  Proud Introducer of these services to Sri Lanka
</h2>


<div className="flex justify-center w-full max-w-4xl px-4">
  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 text-base text-black leading-relaxed w-full">
    {/* Items */}
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Type SCI Installation Method
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Preventive Maintenance
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Bird Proofing
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Semi Automated Cleaning System
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Fully Automated Cleaning System
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Consultation For Domestic PV
    </li>
    <li className="bg-white rounded-full px-6 py-3 text-center" data-aos="fade-up">
      Health Report
    </li>
  </ul>
</div>
</div>

<div data-aos="fade-up">
      <ServicesButtonList className="mt-10" />
    </div>
  
      <div className="flex-1 flex justify-center items-center">

      <div className="flex flex-col items-center justify-center px-4 sm:px-6 md:px-10 lg:px-20 py-10 space-y-6">

      <div className="w-full bg-black py-10">
      <div className="aspect-video w-full">
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/YjEj5eYbUYU?autoplay=1&mute=1&rel=0&modestbranding=1&showinfo=0&controls=1"
          title="YouTube video player"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    </div>


  <h1
    className="text-4xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl mb-8 mt-16 font-bold tracking-tight leading-snug text-black text-center"
    data-aos="fade-up"
  >
    Understanding The Importance of <br className="hidden sm:block" />
    Solar Preventive Maintenance
  </h1>
  <h2
    className="text-4xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl mb-8 font-bold tracking-tight leading-snug text-red-600 text-center"
    data-aos="fade-up"
  >
    Did you know?
  </h2>

  <p
    className="text-base sm:text-lg text-gray-700 leading-7 sm:leading-8 text-justify max-w-4xl"
    data-aos="fade-up"
  >
    They say "an ounce of prevention is better than a pound of cure"
    which is why we get our cars inspected annually and our teeth cleaned on a regular basis. It is cheaper to maintain 
    these things than it is to pay for costly repairs and replacements after the fact. The same applies to solar preventive 
    maintenance. Any number of potential problems can crop up over the many decades your system is in service. Left unresolved, 
    these issues not only prevent you from generating the solar electricity and savings you were expecting, but they can also lead 
    to much costlier repairs later down the line.
  </p>
</div>


  </div>
  <br/>
  <br/>

      <div className="container mx-auto p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 mb-10">
        {/* Left Side - Image */}
        <div data-aos="fade-up">
  <video
    src="https://res.cloudinary.com/dwf8ifbzs/video/upload/v1749474377/WhatsApp_Video_2025-06-09_at_6.33.49_PM_byws29.mp4"
    alt="Solar Installation"
    className="max-w-full h-auto size-4/4 rounded-100 rounded-3xl"
    autoPlay
    muted
    loop
  >
    Your browser does not support the video tag.
  </video>
</div>


        {/* Right Side - Text */}
        <div className="flex flex-col justify-center" data-aos="fade-up">
          <p className="text-lg font-semibold text-gray-700">Renewable solar PV installations</p>
          <h1 className="text-5xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-black">Maintenance</h1>
          <p className="text-gray-700 mb-4 text-justify">
            Bet You Never Knew, preventive maintenance on your photovoltaic
            system will save you for 25- 35 years.
          </p>
          <p className="text-gray-700 mb-4 text-justify">
            Regular servicing and cleaning might cost a bit more upfront. But
            these expenses are dramatically offset by the additional years of
            free electricity and utility bill savings your well-maintained PV
            system will deliver – making the investment well worth it.
          </p>
          <p className="text-gray-700 italic text-justify">
            “Warranty coverage might protect you in some cases. But below is
            just a partial list of common issues that aren’t normally covered by
            manufacturers”
          </p>
          <div className="p-8 flex flex-col justify-center" data-aos="fade-up">
      <h3 className="mt-4 text-2xl font-bold text-red-600">
        Your Solar Panel Warranty will get voided if
      </h3>
      <ul className="mt-4 text-base text-gray-600 list-disc list-outside text-left space-y-6">
        {visibleItems.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
      <button
        className="mt-4 text-blue-600 font-semibold hover:underline self-start"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'See less...' : 'See more...'}
      </button>
    </div>
        </div>
      </div>
      
    </div>

    <div className="container mx-auto p-8  mt-10 mb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Side - Image */}
        <div className="flex flex-col justify-center" data-aos="fade-up">
  <p className="text-lg font-semibold text-gray-700">Renewable</p>
  <h1 className="text-5xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-black">
    Type SCI Installation Method
  </h1>

  <p className="text-xl font-semibold text-gray-800 mb-2 yext-justify">
    Driven by Science. Built for Sustainability. Trusted Worldwide.
  </p>
  <p className="text-gray-700 mb-4 text-justify">
    We don’t just install solar. <br className="sm:hidden" />
    We engineer energy that lasts.
  </p>

  <p className="text-gray-700 mb-4 text-justify">
    At <span className="font-semibold">Solar Care International Private Limited</span>, we specialize in
    high-performance solar solutions for homes and businesses backed by 6,800+ kW of installations,
    advanced TYPE SCI installation Method to international standards.
  </p>

  <h3 className="text-2xl font-bold text-red-600 mb-2">Every system is built with</h3>
  <ul className="list-disc list-outside text-gray-700 space-y-2 mb-4">
    <li><strong>TYPE SCI Method</strong> – Precision-engineered installation</li>
    <li><strong>Aerodynamic Cooling</strong> – Boosts panel efficiency</li>
    <li><strong>Top-Grade Components</strong> – Certified & durable</li>
    <li><strong>Global Standards</strong> – Safety. Performance. Reliability.</li>
    <li><strong>Expert Validation</strong> – Approved by 32 industry specialists</li>
    <li><strong>Tailored Design</strong> – For your rooftop or commercial space</li>
    <li><strong>Total Support</strong> – From consultation to maintenance</li>
  </ul>

  <p className="text-gray-700 italic text-justify">
    Clean energy is only sustainable when installed right. <br />
    <strong>We make it right.</strong>
  </p>
</div>


        {/* Right Side - Video */}
<div className="w-full overflow-hidden" data-aos="fade-up">
  <video
    src="https://res.cloudinary.com/dwf8ifbzs/video/upload/v1749478127/WhatsApp_Video_2025-06-09_at_7.34.59_PM_nrxvha.mp4"
    className="w-full h-auto object-cover rounded-3xl"
    autoPlay
    muted
    loop
    playsInline
  >
    Your browser does not support the video tag.
  </video>
</div>
      </div>
    </div>
    <Statistics className="mt-10"/>
    <section className="flex justify-center">
  <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 mb-16">
    
    {/* Section Heading */}
    <div className="text-center mb-10">
      <h2
        className="text-5xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-black mt-16"
        data-aos="fade-up"
      >
        We are Bonded to Protect and Provide Total Care for Our National Solar Heros 
      </h2>
      {/* <p
        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-4xl mt-4 text-base leading-7 text-gray-600"
        data-aos="fade-up"
      >
        And were Heores Rely on
      </p> */}
    </div>

    {/* First Row - Centered Single Feature */}
    <div className="flex justify-center mb-8">
      <div className="max-w-md p-6 flex flex-col items-center text-center" data-aos="fade-up">
        <h3 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#a1c4fd] via-[#fbc2eb] to-[#fddb92]">
          Domestic, Commercial & Utility Scale
        </h3>
        <p className="mt-4 text-base text-gray-600">
          With Experts of over Decade experience in Local and International Solar PV Industry to protect & provide total solar care for your system.
        </p>
      </div>
    </div>

    {/* Second Row - Two Columns */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  {/* ChemiTek Section */}
  <div className="p-6 flex flex-col items-center text-center gap-4" data-aos="fade-up">
    <a
      href="https://www.chemitek.pt/products"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        src="https://res.cloudinary.com/dwf8ifbzs/image/upload/v1749990263/chemitek-logo_emqcne.png"
        alt="ChemiTek Logo"
        className="w-24 sm:w-32 md:w-40 hover:scale-105 transition-transform duration-300"
      />
    </a>
    <h3 className="text-xl font-bold text-gray-900">Exclusive Authorized Agent</h3>
    <p className="text-gray-600">
      of <strong>ChemiTek</strong> for Sri Lanka, India & Maldives
    </p>
    <p className="text-gray-600">
      SUV TUD certified and approved by top 16 panel manufacturers globally.
    </p>
  </div>

  {/* SolarCleano Section */}
  <div className="p-6 flex flex-col items-center text-center gap-4" data-aos="fade-up">
    <a
      href="https://solarcleano.com/product/solar-panel-cleaning-robot-f1"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        src="https://res.cloudinary.com/dwf8ifbzs/image/upload/v1749989538/Solar-Cleano_zmd7c3.png"
        alt="SolarCleano Logo"
        className="w-24 sm:w-32 md:w-40 hover:scale-105 transition-transform duration-300"
      />
    </a>
    <h3 className="text-xl font-bold text-gray-900">Exclusive Authorized Distributor</h3>
    <p className="text-gray-600">
      of <strong>SolarCleano</strong> for Sri Lanka & Maldives
    </p>
  </div>
</div>

  </div>
</section>

    </>
  );
}

export default Home;